import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";
import { DegreeType } from "../../types/resume";

interface DegreeTypesResponse {
  data: DegreeType[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useDegreeTypes = () => {
  return useQuery({
    queryKey: ["degreeTypes"],
    queryFn: async (): Promise<DegreeType[]> => {
      const response = await USERAXIOS.get<DegreeTypesResponse>(
        "/degree_types"
      );
      return response.data.data;
    },
  });
};

export const useDegreeTypesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["degreeTypes", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<DegreeTypesResponse> => {
      const response = await USERAXIOS.get<DegreeTypesResponse>(
        `/degree_types`,
        {
          params: {
            page: pageParam,
            per_page: pageSize,
            title: searchTerm || undefined,
          },
        }
      );
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const degreeTypes = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: degreeTypes,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
